import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Float, Points, PointMaterial, useGLTF } from "@react-three/drei";
import { useEffect, useMemo, useRef, Suspense, useState } from "react";
import * as THREE from "three";
import { useTheme } from "@/lib/ThemeContext";

/**
 * Stylized whale — rounded body with distort material, tail flukes, spout,
 * pectoral fin, eye. Reactive to mouse hover; sinks as user scrolls.
 */

function useScrollProgress() {
  const [p, setP] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = window.innerHeight;
      const y = window.scrollY;
      const prog = Math.max(0, Math.min(1.35, y / (h * 0.9)));
      setP(prog);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return p;
}

function Whale({ hovered, setHovered, scrollRef }) {
  const group = useRef();
  const spoutRef = useRef();
  const mouse = useRef({ x: 0, y: 0 });
  const { scene } = useGLTF("/balenott8.glb");
  const model = useMemo(() => {
    const cloned = scene.clone(true);
    // il GLB ha un mesh "fluke" extra che duplica la coda (le due parti esterne): lo rimuoviamo
    const extraFluke = cloned.getObjectByName("fluke");
    if (extraFluke && extraFluke.parent) extraFluke.parent.remove(extraFluke);
    // occhio bianco luminoso: sfera emissiva posizionata sul lato sinistro della testa
    const eye = new THREE.Mesh(
      new THREE.SphereGeometry(0.06, 16, 16),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.95 })
    );
    eye.position.set(-0.32, 0.12, 0.18);
    cloned.add(eye);
    cloned.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        if (child.material) {
          child.material.roughness = 0.08;
          child.material.metalness = 0.72;
          if (child.material.color && child.material.color.getHex() === 0x000000) {
            child.material.color.set("#6d28d9");
          }
          if (child.material.emissive) {
            child.material.emissive.set("#7c3aed");
            child.material.emissiveIntensity = 0.5;
          }
        }
      }
    });
    const box = new THREE.Box3().setFromObject(cloned);
    const sizeVec = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const maxDim = Math.max(sizeVec.x, sizeVec.y, sizeVec.z) || 1;
    cloned.position.sub(center);
    return { obj: cloned, normScale: 2.6 / maxDim };
  }, [scene]);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    mouse.current.x += (state.mouse.x - mouse.current.x) * 0.06;
    mouse.current.y += (state.mouse.y - mouse.current.y) * 0.06;

    if (group.current) {
      group.current.position.y = Math.sin(t * 0.6) * 0.14 + (hovered ? Math.sin(t * 4) * 0.08 : 0);
      group.current.position.x = Math.sin(t * 0.3) * 0.12;
      group.current.rotation.y = mouse.current.x * 0.35 + Math.sin(t * 0.25) * 0.08 + (hovered ? Math.sin(t * 3) * 0.15 : 0);
      group.current.rotation.x = -mouse.current.y * 0.18 + Math.sin(t * 0.18) * 0.05;
      group.current.rotation.z = hovered ? Math.sin(t * 2.5) * 0.08 : Math.sin(t * 0.3) * 0.02;

      const base = model.normScale;
      const target = hovered ? base * 1.08 : base;
      const cur = group.current.scale.x;
      group.current.scale.setScalar(cur + (target - cur) * 0.08);

      const p = scrollRef.current;
      group.current.position.y -= Math.min(p, 1) * 0.7;
    }
    if (spoutRef.current) {
      const s = 0.4 + Math.abs(Math.sin(t * 1.4)) * 0.5;
      spoutRef.current.scale.set(s * 0.6, s, s * 0.6);
      spoutRef.current.material.opacity = 0.35 + Math.abs(Math.sin(t * 1.4)) * 0.4;
    }
  });

  return (
    <group
      ref={group}
      scale={model.normScale}
      onPointerOver={(e) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = "pointer"; }}
      onPointerOut={() => { setHovered(false); document.body.style.cursor = "auto"; }}
    >
      <primitive object={model.obj} />
      {/* Water spout */}
      <mesh ref={spoutRef} position={[0, 0.9, 0]}>
        <sphereGeometry args={[0.1, 32, 32]} />
        <meshStandardMaterial color="#e0e7ff" emissive="#93c5fd" emissiveIntensity={1.6} transparent opacity={0.75} roughness={0.05} metalness={0.3} />
      </mesh>
      {/* Occhio luminoso bianco */}
      <mesh position={[0.55, 0.35, 0.42]}>
        <sphereGeometry args={[0.05, 32, 32]} />
        <meshStandardMaterial color="#ffffff" emissive="#ffffff" emissiveIntensity={2.2} roughness={0.05} metalness={0.2} />
      </mesh>
      <OrbitBubbles hovered={hovered} />
    </group>
  );
}
useGLTF.preload("/balenott8.glb");

function OrbitBubbles({ hovered }) {
  const ref = useRef();
  useFrame(({ clock }) => {
    if (!ref.current) return;
    ref.current.rotation.y = clock.elapsedTime * (hovered ? 0.55 : 0.3);
    ref.current.rotation.x = Math.sin(clock.elapsedTime * 0.4) * 0.2;
  });
  const bubbles = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 16; i++) {
      const angle = (i / 16) * Math.PI * 2;
      const r = 2.4 + Math.random() * 0.9;
      arr.push({
        pos: [Math.cos(angle) * r, (Math.random() - 0.5) * 1.6, Math.sin(angle) * r],
        s: 0.05 + Math.random() * 0.14,
      });
    }
    return arr;
  }, []);
  return (
    <group ref={ref}>
      {bubbles.map((b, i) => (
        <Float key={i} speed={2 + Math.random() * 2} rotationIntensity={0.2} floatIntensity={1.4}>
          <mesh position={b.pos} scale={b.s}>
            <sphereGeometry args={[1, 16, 16]} />
            <meshStandardMaterial color="#60a5fa" emissive="#7c3aed" emissiveIntensity={0.55} transparent opacity={0.35} depthWrite={false} />
          </mesh>
        </Float>
      ))}
    </group>
  );
}

function SeaLevel({ scrollRef }) {
  const meshRef = useRef();
  const foamRef = useRef();
  const { viewport } = useThree();
  useFrame(({ clock }) => {
    if (!meshRef.current) return;
    const p = scrollRef.current;
    const baseY = -4;
    const topY = 2.4;
    const y = baseY + (topY - baseY) * Math.min(p, 1);
    const wave = Math.sin(clock.elapsedTime * 1.2) * 0.05;
    meshRef.current.position.y = y + wave;
    if (foamRef.current) {
      foamRef.current.position.y = y + 0.02 + wave;
      foamRef.current.material.opacity = p > 0.02 ? 0.5 : 0;
    }
  });
  return (
    <group>
      <mesh ref={meshRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, -4, 0]}>
        <planeGeometry args={[viewport.width * 3, viewport.height * 3, 32, 32]} />
        <meshStandardMaterial
          color="#1e1b4b"
          emissive="#4c1d95"
          emissiveIntensity={0.35}
          transparent
          opacity={0.72}
          roughness={0.15}
          metalness={0.4}
        />
      </mesh>
      <mesh ref={foamRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, -3.98, 0]}>
        <planeGeometry args={[viewport.width * 3, 0.06]} />
        <meshBasicMaterial color="#a5b4fc" transparent opacity={0} />
      </mesh>
    </group>
  );
}

function ParticleField({ count = 450 }) {
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 22;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 14;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 18 - 4;
    }
    return arr;
  }, [count]);
  const ref = useRef();
  useFrame(({ clock }) => {
    if (ref.current) ref.current.rotation.y = clock.elapsedTime * 0.02;
  });
  return (
    <group ref={ref}>
      <Points positions={positions} stride={3} frustumCulled>
        <PointMaterial
          transparent
          color="#a78bfa"
          size={0.03}
          sizeAttenuation
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          opacity={0.9}
        />
      </Points>
    </group>
  );
}

function Scene({ scrollRef }) {
  const [hovered, setHovered] = useState(false);
  const { camera, size } = useThree();
  const { theme } = useTheme();
  const isLight = theme === "light";
  const bgHex = isLight ? "#ffffff" : "#05050A";
  const fogNear = isLight ? 10 : 8;
  const fogFar = isLight ? 26 : 22;
  useEffect(() => {
    // Push camera back on portrait/narrow viewports so the whale reads smaller.
    const aspect = size.width / size.height;
    const target = aspect < 1 ? 13 : aspect < 1.2 ? 11.5 : 9.5;
    camera.position.z = target;
    camera.updateProjectionMatrix();
  }, [camera, size]);
  return (
    <>
      <color attach="background" args={[bgHex]} />
      <fog attach="fog" args={[bgHex, fogNear, fogFar]} />
      <ambientLight intensity={isLight ? 0.9 : 0.4} color={isLight ? "#ffffff" : "#8b5cf6"} />
      <directionalLight position={[5, 5, 5]} intensity={1.2} color="#a78bfa" />
      <directionalLight position={[-5, -3, -2]} intensity={0.6} color="#3b82f6" />
      <pointLight position={[0, 1, 2]} intensity={1.8} color="#7c3aed" distance={12} />

      <Suspense fallback={null}>
        <Float speed={1.1} rotationIntensity={0.25} floatIntensity={0.9}>
          <Whale hovered={hovered} setHovered={setHovered} scrollRef={scrollRef} />
        </Float>
        <ParticleField />
        <SeaLevel scrollRef={scrollRef} />
      </Suspense>
    </>
  );
}

export default function Whale3D() {
  const scrollProgress = useScrollProgress();
  const scrollRef = useRef(0);
  useEffect(() => { scrollRef.current = scrollProgress; }, [scrollProgress]);

  return (
    <div className="absolute inset-0 pointer-events-auto" data-testid="hero-3d-canvas">
      <Canvas
        dpr={[1, 1.5]}
        camera={{ position: [0, 0.2, 9.5], fov: 42 }}
        gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      >
        <Scene scrollRef={scrollRef} />
      </Canvas>
    </div>
  );
}
